import { observer } from "mobx-react-lite";
import React, { useCallback, useMemo, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import { ColDef, ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import {
    FaComments,
    FaEdit,
    FaPhoneAlt,
    FaSearch,
    FaShareAlt,
    FaSms,
    FaTrashAlt,
    FaWhatsapp,
} from "react-icons/fa";
import { useStore } from "../../../helpers/helpers";
import { htmlValidation } from "../../../helpers/utils";

import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

interface MessageItem {
    id: string;
    channel: string;
    ds: {
        name: string;
        text?: string;
        attachements?: unknown[];
    };
}

interface VoterReachOutListingProps {
    setPopOver: (value: boolean) => void;
    deleteMessage: (id: string) => void;
    search: string;
    onSearchChange: (value: string) => void;
}

const MessageFilterHeader = (params: any) => (
    <div className="flex h-full items-center">
        <span className="truncate text-[11px] font-bold text-slate-600" title={params.displayName}>
            {params.displayName}
        </span>
    </div>
);

const VoterReachOutListing = observer((props: VoterReachOutListingProps) => {
    const { voterReachOutStore } = useStore();
    const { reachOutList, filteredReachoutList } = voterReachOutStore;
    const { setPopOver, deleteMessage, search, onSearchChange } = props;
    const [channelFilter, setChannelFilter] = useState("");

    const messages = useMemo(() => (reachOutList || []) as MessageItem[], [reachOutList]);

    const filteredMessages = useMemo(
        () =>
            ((filteredReachoutList || []) as MessageItem[]).filter(
                (item) => !channelFilter || item.channel === channelFilter
            ),
        [filteredReachoutList, channelFilter]
    );

    const channelCounts = useMemo(
        () =>
            messages.reduce(
                (counts, item) => {
                    if (item.channel === "sms") counts.sms += 1;
                    else if (item.channel === "wa") counts.whatsapp += 1;
                    else if (item.channel === "voicecall") counts.voice += 1;
                    else counts.other += 1;
                    return counts;
                },
                { sms: 0, whatsapp: 0, voice: 0, other: 0 }
            ),
        [messages]
    );

    const summaryCards = [
        {
            label: "Total Messages",
            value: messages.length,
            description: "All saved messages",
            icon: FaComments,
            iconStyle: "bg-indigo-50 text-indigo-600",
            valueStyle: "text-indigo-600",
            borderStyle: "border-indigo-100",
            decorationStyle: "bg-indigo-50",
        },
        {
            label: "SMS",
            value: channelCounts.sms,
            description: "Text messages",
            icon: FaSms,
            iconStyle: "bg-blue-50 text-blue-600",
            valueStyle: "text-blue-600",
            borderStyle: "border-blue-100",
            decorationStyle: "bg-blue-50",
        },
        {
            label: "WhatsApp",
            value: channelCounts.whatsapp,
            description: "WhatsApp messages",
            icon: FaWhatsapp,
            iconStyle: "bg-emerald-50 text-emerald-600",
            valueStyle: "text-emerald-600",
            borderStyle: "border-emerald-100",
            decorationStyle: "bg-emerald-50",
        },
        {
            label: "Voice Calls",
            value: channelCounts.voice,
            description: "Recorded voice messages",
            icon: FaPhoneAlt,
            iconStyle: "bg-violet-50 text-violet-600",
            valueStyle: "text-violet-600",
            borderStyle: "border-violet-100",
            decorationStyle: "bg-violet-50",
        },
    ];

    const editMessage = (message: MessageItem) => {
        const formValue = { ...message.ds, id: message.id };

        if (message.channel === "sms") {
            voterReachOutStore.set_messageType("Text Messages");
            voterReachOutStore.set_textMessageFormValue(formValue);
        } else if (message.channel === "voicecall") {
            voterReachOutStore.set_messageType("Voice Messages");
            voterReachOutStore.set_voiceMessageFormValue(formValue);
        } else if (message.channel === "wa" || message.channel === "sm") {
            voterReachOutStore.set_messageType(
                message.channel === "wa" ? "whatsapp Messages" : "Social Media Post"
            );
            voterReachOutStore.set_whatsappMessageFormValue(formValue);
        } else {
            return;
        }

        voterReachOutStore.set_formType("update");
        voterReachOutStore.set_isSideOverlay(true);
        setPopOver(false);
    };

    const openMessage = (message: MessageItem) => {
        setPopOver(false);
        voterReachOutStore.set_IsMessageOverviewOpen(true);
        voterReachOutStore.set_selected_overview_item(message);
    };

    const getChannelDisplay = useCallback((channel: string) => {
        switch (channel) {
            case "sms":
                return {
                    label: "SMS",
                    icon: <FaSms />,
                    classes: "bg-blue-50 text-blue-700",
                };
            case "voicecall":
                return {
                    label: "Voice Call",
                    icon: <FaPhoneAlt />,
                    classes: "bg-violet-50 text-violet-700",
                };
            case "wa":
                return {
                    label: "WhatsApp",
                    icon: <FaWhatsapp />,
                    classes: "bg-emerald-50 text-emerald-700",
                };
            case "sm":
                return {
                    label: "Social Media",
                    icon: <FaShareAlt />,
                    classes: "bg-indigo-50 text-indigo-700",
                };
            default:
                return {
                    label: "Message",
                    icon: <FaComments />,
                    classes: "bg-slate-100 text-slate-600",
                };
        }
    }, []);

    const columnDefs = useMemo<ColDef<MessageItem>[]>(
        () => [
            {
                headerName: "Message Name",
                field: "ds.name",
                minWidth: 210,
                flex: 1.1,
                sortable: true,
                suppressMenu: true,
                headerComponent: MessageFilterHeader,
                valueGetter: (params: any) => params.data?.ds?.name || "Untitled message",
                cellRenderer: (params: any) => (
                    <button
                        type="button"
                        className="flex h-full max-w-full items-center gap-2 text-left font-semibold text-indigo-600 transition hover:text-indigo-800 hover:underline"
                        title={params.value || "View message"}
                        onClick={(event) => {
                            htmlValidation(event);
                            openMessage(params.data);
                        }}
                    >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                            <FaComments />
                        </span>
                        <span className="truncate">{params.value || "Untitled message"}</span>
                    </button>
                ),
            },
            {
                headerName: "Channel",
                field: "channel",
                minWidth: 145,
                width: 165,
                sortable: true,
                suppressMenu: true,
                headerComponent: MessageFilterHeader,
                cellRenderer: (params: any) => {
                    const channel = getChannelDisplay(params.value);
                    return (
                        <div className="flex h-full items-center">
                            <span
                                className={`inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${channel.classes}`}
                            >
                                {channel.icon}
                                {channel.label}
                            </span>
                        </div>
                    );
                },
            },
            {
                headerName: "Message Preview",
                field: "ds.text",
                minWidth: 240,
                flex: 1.6,
                sortable: false,
                suppressMenu: true,
                headerComponent: MessageFilterHeader,
                valueGetter: (params: any) =>
                    params.data?.channel === "voicecall"
                        ? "Recorded voice message"
                        : params.data?.ds?.text || "—",
                cellRenderer: (params: any) => (
                    <span className="block truncate text-slate-600" title={params.value || ""}>
                        {params.value || "—"}
                    </span>
                ),
            },
            {
                headerName: "Attachments",
                colId: "attachments",
                minWidth: 120,
                width: 135,
                sortable: false,
                suppressMenu: true,
                headerComponent: MessageFilterHeader,
                valueGetter: (params: any) =>
                    params.data?.ds?.attachements?.length
                        ? `${params.data.ds.attachements.length} file${params.data.ds.attachements.length === 1 ? "" : "s"}`
                        : "—",
                cellStyle: { color: "#64748b", fontSize: "12px" },
            },
            {
                headerName: "Actions",
                colId: "actions",
                minWidth: 100,
                width: 110,
                sortable: false,
                suppressMenu: true,
                headerComponent: MessageFilterHeader,
                cellRenderer: (params: any) => (
                    <div className="flex h-full items-center justify-center gap-1">
                        <button
                            type="button"
                            title="Edit message"
                            aria-label={`Edit ${params.data?.ds?.name || "message"}`}
                            onClick={(event) => {
                                htmlValidation(event);
                                editMessage(params.data);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                        >
                            <FaEdit />
                        </button>
                        <button
                            type="button"
                            title="Delete message"
                            aria-label={`Delete ${params.data?.ds?.name || "message"}`}
                            onClick={(event) => {
                                htmlValidation(event);
                                deleteMessage(params.data.id);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-100"
                        >
                            <FaTrashAlt />
                        </button>
                    </div>
                ),
            },
        ],
        [deleteMessage, getChannelDisplay, setPopOver, voterReachOutStore]
    );

    const defaultColDef = useMemo(
        () => ({
            resizable: true,
            sortable: true,
            filter: false,
            suppressMenu: true,
            wrapHeaderText: true,
        }),
        []
    );

    const channelOptions = useMemo(
        () => Array.from(new Set(messages.map((message) => message.channel).filter(Boolean))),
        [messages]
    );

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {summaryCards.map((card) => {
                    const Icon = card.icon;
                    return (
                        <div
                            key={card.label}
                            className={`group relative overflow-hidden rounded-2xl border ${card.borderStyle} bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md`}
                        >
                            <div
                                className={`absolute right-0 top-0 h-24 w-24 -translate-y-7 translate-x-7 rounded-full ${card.decorationStyle} transition-transform duration-200 group-hover:scale-125`}
                            />
                            <div className="relative flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">{card.label}</p>
                                    <p
                                        className={`mt-3 text-3xl font-bold tracking-tight ${card.valueStyle}`}
                                    >
                                        {card.value.toLocaleString()}
                                    </p>
                                    <p className="mt-1 text-xs text-slate-400">{card.description}</p>
                                </div>
                                <div
                                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${card.iconStyle}`}
                                >
                                    <Icon className="text-lg" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[220px] flex-1">
                        <label
                            htmlFor="message-search"
                            className="mb-1.5 block text-[11px] font-semibold text-slate-500"
                        >
                            Search
                        </label>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400" />
                            <input
                                id="message-search"
                                type="search"
                                value={search || ""}
                                onChange={(event) => onSearchChange(event.target.value)}
                                placeholder="Search message name or content..."
                                aria-label="Search messages"
                                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50"
                            />
                        </div>
                    </div>
                    <div className="min-w-[160px]">
                        <label
                            htmlFor="message-channel-filter"
                            className="mb-1.5 block text-[11px] font-semibold text-slate-500"
                        >
                            Channel
                        </label>
                        <select
                            id="message-channel-filter"
                            value={channelFilter}
                            onChange={(event) => setChannelFilter(event.target.value)}
                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50"
                        >
                            <option value="">All Channels</option>
                            {channelOptions.map((channel) => (
                                <option key={channel} value={channel}>
                                    {getChannelDisplay(channel).label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {(channelFilter || search) && (
                        <button
                            type="button"
                            onClick={() => {
                                setChannelFilter("");
                                onSearchChange("");
                            }}
                            className="h-10 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                        >
                            Reset filters
                        </button>
                    )}
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {filteredMessages.length === 0 ? (
                    <div className="flex min-h-[260px] flex-col items-center justify-center px-5 py-10 text-center">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-xl text-slate-400">
                            <FaComments />
                        </div>
                        <h3 className="font-semibold text-slate-800">
                            {messages.length === 0 ? "No messages found" : "No matching messages"}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                            {messages.length === 0
                                ? "Your saved messages will appear here once they have been created."
                                : "Try changing your search or channel filter."}
                        </p>
                        {(channelFilter || search) && (
                            <button
                                type="button"
                                onClick={() => {
                                    setChannelFilter("");
                                    onSearchChange("");
                                }}
                                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div
                        className="ag-theme-quartz w-full"
                        style={
                            {
                                height: "min(70vh, 720px)",
                                minHeight: "420px",
                                "--ag-font-family": "inherit",
                                "--ag-font-size": "12px",
                                "--ag-border-color": "#E2E8F0",
                                "--ag-header-background-color": "#F8FAFC",
                                "--ag-header-foreground-color": "#475569",
                                "--ag-row-hover-color": "#F1F5FF",
                                "--ag-odd-row-background-color": "#FAFBFF",
                                "--ag-row-border-color": "#F1F5F9",
                                "--ag-header-height": "48px",
                                "--ag-row-height": "58px",
                                "--ag-wrapper-border-radius": "0px",
                            } as React.CSSProperties
                        }
                    >
                        <AgGridReact
                            rowData={filteredMessages}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            animateRows
                            suppressCellFocus
                            pagination
                            paginationPageSize={20}
                            headerHeight={48}
                            rowHeight={58}
                        />
                    </div>
                )}
            </div>
        </div>
    );
});

export default VoterReachOutListing;
